import type { TranslationKeys } from './zh';

export const en: TranslationKeys = {
  common: {
    loading: 'Loading...',
    submit: 'Submit',
    cancel: 'Cancel',
    confirm: 'Confirm',
    connectWallet: 'Please connect wallet first',
    userCancelled: 'User cancelled the operation',
    paymentFailed: 'Payment failed',
    uploadFailed: 'Upload failed, please retry',
    uploading: 'Uploading...',
    uploaded: '✓ Logo uploaded',
    waitingUpload: 'Waiting to upload...',
    connectWalletToUpload: 'Please connect wallet to upload',
    selectImage: 'Click to select logo (max 5MB)',
    selectImageFile: 'Please select an image file',
    fileTooLarge: 'File size cannot exceed 5MB',
    transactionSent: 'Transaction sent, waiting for confirmation...',
    paymentConfirmed: 'Payment confirmed',
    paymentSuccess: 'Payment successful!',
    deployed: 'Deployed',
    submitted: 'Submitted',
    sendingTransaction: 'Sending transaction...',
    waitingConfirmation: 'Waiting for confirmation...',
    viewOnChain: 'View on chain',
    viewContract: 'View contract',
    visit: 'Visit',
    beFirstSubmitter: 'Be the first to submit',
  },
  nav: {
    home: 'Home',
    deployToken: 'Deploy Token',
    dappEntry: 'DApp Entry',
    tokenList: 'Token List',
    admin: 'Admin',
    contact: 'Contact Us',
    connectWallet: 'Connect Wallet',
  },
  contact: {
    title: 'Contact Us',
    description: 'For any questions or partnership inquiries, feel free to reach out',
    emailLabel: 'Official Email',
    responseTime: 'We will respond to your email within 24 hours',
    sendEmail: 'Send Email',
  },
  home: {
    title: 'MaToken Web3 Wallet Service',
    titlePrefix: 'Next-Gen',
    multiChainSupport: 'Multi-Chain Support',
    subtitle: 'Secure and convenient decentralized digital wallet supporting multi-chain asset management',
    coreFeatures: 'Core Features',
    coreFeaturesDesc: 'Providing you with secure and convenient digital asset management',
    secureStorage: 'Secure Storage',
    secureStorageDesc: 'Local encrypted keys',
    multiChain: 'Multi-Chain',
    multiChainDesc: 'Support major chains',
    easyUse: 'Easy to Use',
    easyUseDesc: 'Clean intuitive UI',
    dappBrowser: 'DApp Browser',
    dappBrowserDesc: 'Built-in Web3 browser',
    downloadNow: 'Download Now',
    downloadDesc: 'Choose your platform to start using MaToken Wallet',
    iosVersion: 'iOS Version',
    iosDesc: 'Install beta via TestFlight',
    testflightDownload: 'Download on TestFlight',
    androidVersion: 'Android Version',
    androidDesc: 'Support Android 6.0 and above',
    apkDownload: 'Download APK',
    scanToDownload: 'Scan to download',
    versionInfo: 'Version Info',
    currentVersion: 'Current Version:',
    platform: 'Platform:',
    updateTime: 'Updated:',
    fileSize: 'File Size:',
    installGuide: 'Installation Guide',
    iosInstall: 'iOS Installation',
    androidInstall: 'Android Installation',
    iosStep1: 'Click download button to open TestFlight',
    iosStep2: 'Install TestFlight from App Store if needed',
    iosStep3: 'Accept and install in TestFlight',
    androidStep1: 'Click to download the APK file',
    androidStep2: 'Open file, allow "Unknown sources" install',
    androidStep3: 'Tap "Install" to complete',
    supportedChains: 'Supported Chains',
    web3Services: 'Web3 Services',
    web3ServicesDesc: 'Listed tokens and DApps will be displayed directly in MaToken Wallet',
    deployToken: 'Deploy Token',
    deployTokenDesc: 'One-click token deployment based on factory contracts',
    dappEntry: 'DApp Entry',
    dappEntryDesc: 'Submit your DApp for more users to discover',
    tokenList: 'Token Listing',
    tokenListDesc: 'List your token on the platform for more exposure',
    oneToken: '1 Token',
    deployedTokens: 'Deployed Tokens',
    deployedTokensDesc: 'Total tokens deployed via platform',
    dappCount: 'DApp Count',
    dappCountDesc: 'Total DApps submitted to platform',
    listedTokens: 'Listed Tokens',
    listedTokensDesc: 'Total tokens listed on platform',
    comingSoon: 'Coming Soon',
    noApkAvailable: 'No version available',
  },
  deployToken: {
    title: 'Deploy Token',
    description: 'Quickly deploy ERC20 tokens based on factory contracts',
    tokenInfo: 'Token Info',
    tokenInfoDesc: 'Fill in token basic information',
    selectChain: 'Select Chain',
    chainHint: 'Supports Ethereum Mainnet and Sepolia Testnet',
    ethereumMainnet: 'Ethereum Mainnet',
    ethereumSepolia: 'Ethereum Sepolia (Testnet)',
    tokenLogo: 'Token Logo',
    tokenName: 'Token Name',
    tokenNamePlaceholder: 'e.g. My Token',
    tokenSymbol: 'Token Symbol',
    tokenSymbolPlaceholder: 'e.g. MTK',
    tokenSymbolHint: 'Recommend 3-6 uppercase letters',
    totalSupply: 'Total Supply',
    totalSupplyPlaceholder: '1000000',
    totalSupplyHint: 'Total token supply (18 decimals)',
    feeInfo: 'Fee Info',
    deployFee: 'Deploy Fee',
    feeDesc: 'Fee will be deducted automatically during deployment. You will own the token contract after successful deployment.',
    notes: 'Notes',
    note1: 'Token symbol cannot be changed after deployment',
    note2: 'Set total supply based on actual needs',
    note3: 'Keep the contract address safe after deployment',
    payAndDeploy: 'Pay {amount} tokens and deploy',
    deployHint: 'Click to login first, then pay and deploy. Token will be indexed automatically after successful deployment.',
    deployConfirmed: 'Deployment confirmed, indexing token...',
    deploySuccess: 'Token deployed successfully!',
    tokenAddress: 'Token Address',
    switchNetwork: 'Please switch to {network} network',
    fillAllFields: 'Please fill in all fields',
  },
  dapp: {
    listTitle: 'DApp List',
    listDesc: 'Discover and explore excellent decentralized applications',
    submitDapp: 'Submit DApp',
    noDapps: 'No DApps yet',
    createTitle: 'DApp Entry',
    createDesc: 'Submit your DApp to the platform for more users to discover',
    dappInfo: 'DApp Info',
    dappInfoDesc: 'Fill in your DApp basic information',
    dappName: 'DApp Name',
    dappNamePlaceholder: 'e.g. My DApp',
    dappDescription: 'Description',
    dappDescriptionPlaceholder: 'Briefly describe your DApp features...',
    dappUrl: 'URL',
    dappUrlPlaceholder: 'https://example.com',
    dappCategory: 'Category',
    dappCategoryPlaceholder: 'DeFi, NFT, GameFi, Social, etc.',
    dappLogo: 'DApp Logo',
    payAndSubmit: 'Pay and Submit',
    payAndSubmitBtn: 'Pay {amount} tokens and submit',
    submitHint: 'Click to pay and submit. DApp will be submitted automatically after successful payment.',
    submittingDapp: 'Payment confirmed, submitting DApp...',
    submitSuccess: 'Payment successful! DApp submitted, pending admin review.',
    feeInfo: 'Fee Info',
    listingFee: 'Listing Fee',
    feeDesc: 'Admin review required after submission. Your DApp will be displayed after approval.',
    notes: 'Notes',
    note1: 'Ensure URL is accessible',
    note2: 'Choose accurate category for easy discovery',
    note3: 'Check review status in DApp list after submission',
    fillRequired: 'Please fill in required fields',
  },
  token: {
    listTitle: 'Token List',
    listDesc: 'View all deployed and listed tokens',
    listToken: 'List Token',
    listedTokens: 'Listed Tokens',
    deployedTokens: 'Deployed Tokens',
    noListedTokens: 'No listed tokens yet',
    noDeployedTokens: 'No deployed tokens yet',
    chainId: 'Chain ID',
    contractAddress: 'Contract Address',
    deployTime: 'Deploy Time',
    createTitle: 'Token Listing',
    createDesc: 'List your token on the platform for more exposure',
    tokenInfo: 'Token Info',
    tokenInfoDesc: 'Fill in token contract address',
    tokenLogo: 'Token Logo',
    tokenAddress: 'Token Contract Address',
    tokenAddressPlaceholder: '0x...',
    payAndSubmit: 'Pay and Submit',
    payAndSubmitBtn: 'Pay {amount} tokens and submit',
    submitHint: 'Click to pay and submit. Token will be listed automatically after successful payment.',
    submittingToken: 'Payment confirmed, listing token...',
    submitSuccess: 'Payment successful! Token listed.',
    feeInfo: 'Fee Info',
    listingFee: 'Listing Fee',
    feeDesc: 'Your token will be displayed in the token list after listing.',
    notes: 'Notes',
    note1: 'Ensure contract address is correct and is an ERC20 token',
    note2: 'Token must be deployed on supported chains',
    note3: 'Check token list after listing',
    fillTokenAddress: 'Please fill in token contract address',
    txHash: 'Transaction Hash',
    paymentReceiver: 'Payment Receiver',
  },
  errors: {
    loginFailed: 'Login failed, unable to get JWT token',
    chainNotSupported: 'Current chain does not support this token, please switch to Sepolia testnet',
    paymentReceiverNotConfigured: 'Payment receiver not configured, please contact admin',
    walletNotConnected: 'Wallet not connected, please connect wallet first',
    transactionInProgress: 'Transaction in progress, please wait...',
    switchToChain: 'Please switch to chain ID {chainId} network',
    manualSwitchChain: 'Please manually switch to target chain',
  },
};
